import { TeamRoleService } from '../../services/team-role/team-role.service';
import { StandardIptService } from '../../services/standard-ipt/standard-ipt.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { TeamMemberEntry, TeamRoleUtil } from 'src/app/shared/TeamRoleUtil';
import { ProjectPhase, phaseToLabel } from 'src/app/shared/model/general/ProjectPhase';
import { Technology, technologyToLabel } from 'src/app/shared/model/general/Technology';
import { Country } from 'src/app/shared/model/geo/Country';
import { IptPhase, iptPhaseToIndex, iptPhaseToLabel, iptPhaseToProjectPhase } from 'src/app/shared/model/team/IptPhase';
import { GetUsers } from 'src/app/shared/services/user/GetUsers';
import { StandardIpt } from 'src/app/shared/model/standard-ipt/StandardIpt';
import { UserService } from 'src/app/shared/services/user/user.service';
import { GetTeamRoles } from 'src/app/shared/services/team-role/GetTeamRoles';
import { TeamMember } from '../../model/team/TeamMember';
import { CountryAlpha2Code } from '../../model/geo/CountryAlpha2Code';


@Component({
  selector: 'digires-standard-ipt-creation',
  templateUrl: './standard-ipt-creation.component.html',
  styleUrls: ['./standard-ipt-creation.component.scss'],
})

export class StandardIptCreationComponent implements OnInit, OnDestroy {
  destroy$: Subject<boolean> = new Subject<boolean>();

  readonly allTechnologies: Technology[] = Object.values(Technology);
  readonly allProjectPhases: ProjectPhase[] = Object.values(ProjectPhase);
  readonly allIptPhases: IptPhase[] = Object.values(IptPhase);
  readonly iptPhaseToLabel = iptPhaseToLabel;

  readonly iptPhaseToUtil: Record<IptPhase, TeamRoleUtil> = {
    [IptPhase.DEVELOPMENT_INITIAL]: new TeamRoleUtil(IptPhase.DEVELOPMENT_INITIAL),
    [IptPhase.DEVELOPMENT_CLOSING]: new TeamRoleUtil(IptPhase.DEVELOPMENT_CLOSING),
    [IptPhase.CONSTRUCTION]: new TeamRoleUtil(IptPhase.CONSTRUCTION),
    [IptPhase.OPERATIONS]: new TeamRoleUtil(IptPhase.OPERATIONS)
  };

  userMap: {[userId: string]: GetUsers};
  roleMap: {[roleId: string]: GetTeamRoles};
  allRoles: GetTeamRoles[];
  allUsers: GetUsers[];
  formGroup: FormGroup;
  iptEntries: StandardIptEntry[] = [];
  isWaiting = false;

  constructor (
    private standardIptService: StandardIptService,
    private teamRoleService: TeamRoleService,
    private userService: UserService,
  ) {
  }

  ngOnInit(): void {
    this.loadUsersAndRoles();
    this.formGroup = new FormGroup({
      technology: new FormControl(),
      country: new FormControl(),
      projectPhase: new FormControl(),
    });

    this.formGroup
      .valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onFgChanged());
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  getTechnologyLabel(technology: Technology): string {
    return technologyToLabel[technology];
  }

  getProjectPhaseLabel(phase: ProjectPhase): string {
    return phaseToLabel[phase];
  }

  isSaveAndResetDisabled(): boolean {
    return this.iptEntries.length !== 0;
  }

  private getSelectionData(): {valid: boolean, tech: Technology, cc: CountryAlpha2Code, phase: ProjectPhase} {
    const fgValues = this.formGroup.value;
    const technology: Technology = fgValues.technology;
    const country: Country = fgValues.country;
    const projectPhase: ProjectPhase = fgValues.projectPhase;
    return {
      valid: !!technology && !!country?.alpha2Code && !!projectPhase,
      tech: technology,
      cc: country?.alpha2Code,
      phase: projectPhase,
    };
  }

  hasUnsavedChanges(): boolean {
    for (const iptEntry of this.iptEntries) {
      const oldMembers = iptEntry.ipt.members;
      const newMembers = this.createMemberDataForSave(iptEntry);
      if (oldMembers.length !== newMembers.length) {
        return true;
      }
      const oldMembersSorted = this.sortByUserIdAndRoleId(oldMembers);
      const newMembersSorted = this.sortByUserIdAndRoleId(newMembers);
      for (let i = 0; i < oldMembersSorted.length; i++) {
        const oldMember = oldMembersSorted[i];
        const newMember = newMembersSorted[i];
        if (oldMember.userId !== newMember.userId || oldMember.roleId !== newMember.roleId) {
          return true;
        }
      }
    }
    return false;
  }

  private onFgChanged() {
    const data = this.getSelectionData();
    if (data.valid) {
      this.loadStandardIpt(data.tech, data.cc, data.phase);
    }
  }

  private async loadUsersAndRoles() {
    const results = await Promise.all([
      this.userService.fetchAllUsers(),
      this.teamRoleService.fetchAllTeamRolesAsMap(),
    ]);
    this.userMap = results[0];
    this.roleMap = results[1];
    this.allRoles = Object.values(this.roleMap);
    this.allUsers = Object.values(this.userMap);
  }

  onResetClicked() {
    const data = this.getSelectionData();
    if (data.valid) {
      this.loadStandardIpt(data.tech, data.cc, data.phase);
    }
  }

  private loadStandardIpt(technology: Technology, countryCode: CountryAlpha2Code, projectPhase: ProjectPhase) {
    this.isWaiting = true;
    this.standardIptService.getStandardIptFor(technology, countryCode, projectPhase)
      .then(ipts => this.createAllIptEntries(ipts))
      .finally(() => this.isWaiting = false);
  }

  async onSaveClicked(): Promise<boolean> {
    if (!this.isSaveAndResetDisabled()) {
      console.error('onSaveClicked.isEnabled=false');
      return false;
    }
    this.isWaiting = true;
    return this.save()
      .then( () => true)
      .catch(() => false)
      .finally(() => this.isWaiting = false);
  }

  private async save(): Promise<void> {
    const iptsToSave = this.iptEntries.map( entry => {
      return {
        ...entry.ipt,
        members: this.createMemberDataForSave(entry),
      };
    });
    const technology: Technology = iptsToSave[0].technology;
    const countryCode: CountryAlpha2Code = iptsToSave[0].countryCode;
    const projectPhase: ProjectPhase = iptPhaseToProjectPhase[iptsToSave[0].phase];

    return this.standardIptService.updateStandardIptFor(
      iptsToSave,
      technology,
      countryCode,
      projectPhase,
    )
      .then(newIpts => this.createAllIptEntries(newIpts))
      .catch(error => Promise.reject(error));
  }

  private createMemberDataForSave(iptEntry: StandardIptEntry): TeamMember[] {
    const members: TeamMember[] = [];
    for (const memberEntry of iptEntry.memberEntries) {
      const user = memberEntry.getUser();
      const role = memberEntry.getRole();
      if (user && role) {
        members.push({userId: user.id, roleId: role.id, disableEmails: false});
      }
    }
    return members;
  }

  private sortByUserIdAndRoleId(members: TeamMember[]): TeamMember[] {
    return members.sort((a, b) => {
      const sortByUserId = a.userId.localeCompare(b.userId);
      if (sortByUserId === 0) {
        return a.roleId.localeCompare(b.roleId);
      }
      return sortByUserId;
    });
  }

  private createAllIptEntries(standardIpts: StandardIpt[]) {
    this.iptEntries = standardIpts
      .sort((a, b) => iptPhaseToIndex(a.phase) - iptPhaseToIndex(b.phase))
      .map(ipt => this.createIptEntry(ipt));
  }

  private createIptEntry(standardIpt: StandardIpt): StandardIptEntry {
    const util = this.iptPhaseToUtil[standardIpt.phase];
    return {
      ipt: standardIpt,
      memberEntries: util.createEntriesForTeam(standardIpt.members, this.userMap, this.roleMap),
    };
  }

  isExportDisabled(): boolean {
    const data = this.getSelectionData();
    return this.isWaiting || !data.valid;
  }

  onExportClicked() {
    const data = this.getSelectionData();
    if (data.valid) {
      this.isWaiting = true;
      this.standardIptService.downloadStandardIptExcel(data.tech, data.cc)
        .finally(() => this.isWaiting = false);
    }
  }

}
 export interface StandardIptEntry {
  ipt: StandardIpt;
  memberEntries: TeamMemberEntry[];
}
